import { notification, TableProps } from 'antd';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import VersionHistoryFooter from '../../../foundation/components/version_history_footer/VersionHistoryFooter';
import { formatPercentValue } from '../../../foundation/utils/percentageHandler';
import {
  addItemToStorage,
  getItemFromStorage,
} from '../../../foundation/utils/storageHandler';
import { useAppDispatch } from '../../../store/hooks';
import {
  useGetVariantFrequenciesQuery,
  useGetVariantLinkoutsQuery,
  useUpdatePopulationAlleleFrequencyMutation,
} from '../../api/variantApi';
import LoadMoreBtn from '../components/LoadMoreBtn';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import { useVariantAccess } from '../hooks/useVariantAccess';
import {
  setFrequencyDataNeedsRecalculation,
  VariantAFPopulation,
} from '../redux';
import { hasFrequencyDataChanged } from '../utils';

const LOAD_MORE_CUTTOFF_LENGTH = 1;
const PopulationSection = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const [updatePopulationAlleleFrequency] =
    useUpdatePopulationAlleleFrequencyMutation();
  const [showMore, setShowMore] = useState(false);
  const [isPopulationLoading, setIsPopulationLoading] = useState(false);

  // Read frequencies straight from the query hook's cache rather than a selector
  const { data: variantAlleFrequenciesData } = useGetVariantFrequenciesQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  // Read linkouts straight from the query hook's cache rather than a selector
  const { data: linkouts } = useGetVariantLinkoutsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { canEditWorkflow } = useVariantAccess();
  const { stageType } = useParams();
  const stageInfo = useVariantStep(
    stageType || VARIANT_STAGE_TYPES.FREQUENCY_DATA,
  );

  const dataSource = variantAlleFrequenciesData?.frequencies || [];

  const versioningInfo = variantAlleFrequenciesData?.versioning_info;

  const columns: TableProps<VariantAFPopulation>['columns'] = useMemo(
    () => [
      {
        title: 'Population',
        dataIndex: 'population',
        key: 'population',
        render: (text) => (
          <span>
            <strong className="h-bold-column">{text}</strong>
          </span>
        ),
      },
      {
        title: 'Allele count',
        dataIndex: 'allele_count',
        key: 'allele_count',
      },
      {
        title: 'Allele number',
        dataIndex: 'allele_number',
        key: 'allele_number',
      },
      {
        title: 'Homozygous',
        dataIndex: 'homozygous',
        key: 'homozygous',
      },
      {
        title: 'Hemizygous',
        dataIndex: 'hemizygous',
        key: 'hemizygous',
      },
      {
        title: 'Allele frequency [%]',
        dataIndex: 'allele_frequency_percentage',
        key: 'allele_frequency_percentage',
      },
      {
        title: 'Filtered AF %',
        dataIndex: 'filtered_allele_frequency',
        key: 'filtered_allele_frequency',
        // When the joint FAF95 (exome + genome) value is filled, it replaces
        // the exome/genome filtered AF shown by filtered_allele_frequency.
        render: (value: string, record: VariantAFPopulation) => {
          const jointValue = record.fafmax_faf95_max_joint?.trim();
          if (jointValue) {
            return formatPercentValue(parseFloat(jointValue));
          }
          return value;
        },
      },
    ],
    [],
  );

  const tableDataSource = useMemo(() => {
    if (showMore) {
      return dataSource;
    }
    return dataSource.slice(0, LOAD_MORE_CUTTOFF_LENGTH);
  }, [showMore, dataSource]);

  const handleShowMoreClick = () => {
    setShowMore((v) => !v);
  };

  const handleUpdateDataClick = async () => {
    // Prevent multiple simultaneous updates
    if (isPopulationLoading) {
      return;
    }
    try {
      if (params.vid) {
        setIsPopulationLoading(true);

        // Capture the current frequencies BEFORE the update so we can diff
        const oldFrequencies = variantAlleFrequenciesData?.frequencies || [];

        const data = await updatePopulationAlleleFrequency({
          variant_id: params.vid,
        }).unwrap();

        if (data) {
          const newFrequencies = data.frequencies || [];

          // Only flag for recalculation when the frequency values actually changed
          if (hasFrequencyDataChanged(oldFrequencies, newFrequencies)) {
            dispatch(setFrequencyDataNeedsRecalculation(true));
          }

          // Save the date to local storage to persist it on reload
          if (params.vid && data.versioning_info?.date) {
            addItemToStorage(
              `gnomad_update_date_${params.vid}`,
              data.versioning_info.date,
            );
          }

          notification.success({
            message: NOTIFICATION_MESSAGES.SUCCESS,
            description: NOTIFICATION_MESSAGES.POPULATION_AF_UPDATED,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsPopulationLoading(false);
    }
  };

  const historyInfo = useMemo(() => {
    // Try to get date from versioningInfo (Redux) first, then fallback to storage (survives reload)
    const storedDate = params.vid
      ? getItemFromStorage(`gnomad_update_date_${params.vid}`)
      : null;
    const date = versioningInfo?.date || storedDate || '';
    // If we have a date, we use the "source on date" format.
    // We check if source_version is available, otherwise fallback to source from first frequency.
    let source =
      versioningInfo?.source_version ||
      dataSource.find((f) => f.source && f.source.trim())?.source ||
      '';
    // If we have a date but the source doesn't end with "on", we append " on" to match the PUT behavior
    if (date && source && !source.toLowerCase().endsWith(' on')) {
      source = `${source} on`;
    }
    return `${source} ${date}`.trim();
  }, [versioningInfo, dataSource, params.vid]);

  return (
    <>
      <div className="frequency-data__population-header">
        <h3>Allele frequency in population database gnomAD</h3>
        <div className="interpretation__intervar-link">
          <span
            className="interpretation__intervar-link__anchor"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (linkouts?.gnomad_url_hg19) {
                window.open(
                  linkouts.gnomad_url_hg19,
                  '_blank',
                  'noopener,noreferrer',
                );
              }
              if (linkouts?.gnomad_url_hg38) {
                window.open(
                  linkouts.gnomad_url_hg38,
                  '_blank',
                  'noopener,noreferrer',
                );
              }
            }}
          >
            gnomAD
          </span>
        </div>
      </div>
      <ResizableTable
        columns={columns}
        dataSource={tableDataSource}
        pagination={false}
        rowKey={(record) => record.id}
        loading={isPopulationLoading}
        persistenceKey="population-frequencies"
      />
      <VersionHistoryFooter
        historyInfo={historyInfo}
        onUpdateClick={handleUpdateDataClick}
        // disable if user is not logged in or stage is accepted or variant is locked
        disabled={!canEditWorkflow || stageInfo?.status === 'accepted'}
      />
      {dataSource.length > LOAD_MORE_CUTTOFF_LENGTH && (
        <LoadMoreBtn
          showMore={showMore}
          handleShowMoreClick={handleShowMoreClick}
        />
      )}
    </>
  );
};

export default PopulationSection;
